/**
 * OverView extra voters (R120) — three additional, STATEFUL/LATCHED OverView
 * constituents on top of the original 10:
 *
 *   • bolevD / bolevW — Breakout-Level bias. From the chart bar whose CLOSE
 *     touches the upper (BUY) line → +1, held until a bar whose CLOSE touches
 *     the lower (SHORT) line → −1, and vice-versa. 'na' before the first touch.
 *     Levels come from Daily (bolevD) / Weekly (bolevW) CLH candles via the same
 *     `computeBolev` the chart's BOLev overlay draws.
 *
 *   • vol — Weighted-Volume spike bias. From a GREEN (bull) spike bar → +1, held
 *     until a RED (bear) spike bar → −1, and vice-versa. 'na' before the first
 *     spike. Uses the same `computeWVol` the Vol pane / AV_Band spikes use.
 *
 *   • avBand — AV_Band bias. close vs the Fast HMA of the Effective Line, with
 *     the Effective Line resolved by **Logic 2** (strong-up → zone LOW,
 *     strong-down → zone HIGH, else mid) against the OverView score EXCLUDING
 *     avBand itself (the `baseScore`) — this breaks the circular dependency
 *     (AV_Band's line is derived from the score, yet avBand feeds the score).
 *
 * Pure helpers shared by the single-value and per-bar OverView engines so the
 * chart, scan, backtest and autotrade all vote identically. A byte-for-byte
 * port lives at backend/src/services/strategies/chartPort/overviewExtraVoters.ts.
 */
import { CandleRow } from './dailyBlastIndicators';
import { computeBolev } from './bolev';
import { computeWVol } from './wvol';
import { hmaSeries } from './hma';
import type { TrendDir } from '../theme/overviewColors';

/** Resolve the BOLev lookback from chart settings (1..10, default 2). */
export function resolveBolevLookback(settings: any): number {
  return Math.max(1, Math.min(10, Math.floor(Number(settings?.bolev?.lookback) || 2)));
}

/**
 * Latched BO voter. Per chart bar, find the active Daily/Weekly period (latest
 * period.time ≤ bar time), then:
 *   close ≥ buyLevel   → upper touch → latch +1 ('up')
 *   close ≤ shortLevel → lower touch → latch −1 ('down')
 *   otherwise          → hold the previous latch ('na' until the first touch)
 *
 * @param periodCandles RAW Daily (bolevD) or Weekly (bolevW) candles, IST-shifted.
 */
export function bolevDirSeries(
  candles: CandleRow[],
  periodCandles: CandleRow[] | null | undefined,
  lookback: number,
): TrendDir[] {
  const n = candles.length;
  const out: TrendDir[] = new Array(n).fill('na');
  if (!periodCandles || periodCandles.length < lookback + 1) return out;
  const periods = computeBolev(periodCandles as any, lookback).periods.slice().sort((a, b) => a.time - b.time);
  if (!periods.length) return out;
  let cur = -1;
  let state: TrendDir = 'na';
  for (let i = 0; i < n; i++) {
    const t = candles[i].time;
    while (cur + 1 < periods.length && periods[cur + 1].time <= t) cur++;
    if (cur < 0) { out[i] = 'na'; continue; }
    const p = periods[cur];
    const close = candles[i].close;
    if (p.buyLevel != null && close >= p.buyLevel) state = 'up';
    else if (p.shortLevel != null && close <= p.shortLevel) state = 'down';
    out[i] = state;
  }
  return out;
}

/**
 * Latched Vol voter. Green (bull) WVol spike → latch +1; red (bear) spike →
 * latch −1; non-spike bars hold; 'na' before the first spike.
 */
export function volDirSeries(candles: CandleRow[]): TrendDir[] {
  const n = candles.length;
  const out: TrendDir[] = new Array(n).fill('na');
  const wv = computeWVol(candles as any);
  let state: TrendDir = 'na';
  for (let i = 0; i < n; i++) {
    const p = wv[i];
    if (p && p.isSpike) state = p.isBull ? 'up' : 'down';
    out[i] = state;
  }
  return out;
}

/**
 * AV_Band voter — close vs Fast HMA of the Effective Line (Logic 2, always).
 * `baseScoreByTime` MUST be the OverView score EXCLUDING avBand, keyed by bar
 * time (IST-shifted seconds), so the line that feeds this voter is identical to
 * the line the engine resolves and (when displayed) the chart draws.
 */
export function avBandDirSeries(
  candles: CandleRow[],
  baseScoreByTime: Map<number, number>,
  settings: any,
): TrendDir[] {
  const n = candles.length;
  const out: TrendDir[] = new Array(n).fill('na');
  if (!n) return out;
  const acfg = (settings && settings.avBand) || {};
  const fastP = Math.max(2, Math.min(400, Math.floor(Number(acfg.fastPeriod) || 14)));
  // Effective Line rule — mirror the DRAWN AV_Band overlay's setting so the voter
  // and the band always agree. logic2: strong-up → zone LOW, strong-down → HIGH.
  // logic1 (default): strong-up → HIGH, strong-down → LOW. (sc from the base
  // score, i.e. OverView excluding avBand.)
  const logic2 = acfg.effectiveMode === 'logic2';

  const wv = computeWVol(candles as any);
  const eff: number[] = new Array(n).fill(NaN);
  let zHigh = NaN, zLow = NaN;
  for (let i = 0; i < n; i++) {
    if (wv[i] && wv[i].isSpike) { zHigh = Number(candles[i].high); zLow = Number(candles[i].low); }
    if (!Number.isFinite(zHigh) || !Number.isFinite(zLow)) continue;
    const sc = baseScoreByTime.get(candles[i].time);
    if (sc != null && sc > 20) eff[i] = logic2 ? zLow : zHigh;
    else if (sc != null && sc < -20) eff[i] = logic2 ? zHigh : zLow;
    else eff[i] = (zHigh + zLow) / 2;
  }
  const fastH = hmaSeries(eff, fastP);
  for (let i = 0; i < n; i++) {
    const h = fastH[i];
    if (!Number.isFinite(h)) { out[i] = 'na'; continue; }
    const close = candles[i].close;
    out[i] = close > h ? 'up' : close < h ? 'down' : 'neutral';
  }
  return out;
}
